import { Button, ButtonStyles, Dropdown, Icon, Icons, Label, Popup, TextInput } from '@silverfort/sf-components';
import { Dispatch, FC, memo, SetStateAction, SVGProps, useCallback, useEffect, useState } from 'react';
import CopyIcon from '../../../Controls/Buttons/CopyIcon';
import { APP_USER_PERMISSIONS, PUBLIC_API_CATEGORIES } from '@silverfort-nodelibs/common/utils/consts';

interface AppUser {
    name: string;
    category: number;
    permissions: string;
    app_user_id: string;
    app_user_secret: string;
    created_by: string;
    last_login_time: string;
}

interface CreateKeyPopupProps {
    open: boolean;
    onClose: () => void;
    appUser: AppUser;
    createAppUser: (appUser: AppUser) => void;
    validateAppUser: (appUser: AppUser) => AppUserValidation[];
}

interface AppUserValidation {
    error: string;
    field: keyof AppUser;
}

interface CreateKeyContentProps {
    appUserState: AppUser;
    setAppUserState: Dispatch<SetStateAction<AppUser>>;
    validations: AppUserValidation[];
}

interface CreateKeyFooterProps {
    onClose: () => void;
    onSave: () => void;
}

const CreateKeyFooter: FC<CreateKeyFooterProps> = memo(({ onClose, onSave }) => (
    <div className="flex w-full justify-between">
        <div className="flex items-center gap-1">
            <Icon icon={Icons.Warning as FC<SVGProps<SVGSVGElement>>} className="text-sf-neutral-500" />
            <span className="text-sf-gray-500 text-13 font-sf font-normal leading-4">
                Save App User Secret. You won’t be able to see it again.
            </span>
        </div>
        <div className="flex justify-end gap-2 ">
            <Button buttonStyle={ButtonStyles.GhostSolid} text="Cancel" onClick={onClose} />
            <Button buttonStyle={ButtonStyles.PrimarySolid} text="Create" onClick={onSave} />
        </div>
    </div>
));

const CreateKeyContent: FC<CreateKeyContentProps> = memo(({ appUserState, setAppUserState, validations }) => {
    const [nameValidation, setNameValidation] = useState<string>();
    const [categoryValidation, setCategoryValidation] = useState<string>();
    const [permissionsValidation, setPermissionsValidation] = useState<string>();

    useEffect(() => {
        setNameValidation(validations.find(({ field }) => field === 'name')?.error);
        setCategoryValidation(validations.find(({ field }) => field === 'category')?.error);
        setPermissionsValidation(validations.find(({ field }) => field === 'permissions')?.error);
    }, [validations]);

    const apiTypes = Object.values(PUBLIC_API_CATEGORIES).map(({ value, label }) => ({ value: +value, title: label }));
    const permissions = Object.values(APP_USER_PERMISSIONS).map(({ value, label }) => ({ value, title: label }));

    const setPermissions = useCallback(
        (value?: string | string[]) => {
            if (!value) {
                return;
            }
            const permissions = Array.isArray(value) ? value[0] : value;
            setAppUserState((prevState) => ({ ...prevState, permissions }));
            setPermissionsValidation(undefined);
        },
        [setAppUserState]
    );

    const setApiType = useCallback(
        (value?: number | number[]) => {
            if (value === undefined || value === null) {
                return;
            }
            const category = Array.isArray(value) ? value[0] : value;
            setAppUserState((prevState) => ({ ...prevState, category }));
            setCategoryValidation(undefined);
        },
        [setAppUserState]
    );

    const setApiName = useCallback(
        (name: string) => {
            setAppUserState((prevState) => ({ ...prevState, name }));
            setNameValidation(undefined);
        },
        [setAppUserState]
    );

    return (
        <div className="flex w-full">
            <div className="flex flex-col gap-6 pr-6 border-r box-border py-6">
                <div>
                    <Label text="API name" mandatory className="mb-2" />
                    <TextInput
                        placeholder="Add API name"
                        onChange={setApiName}
                        value={appUserState.name}
                        validationMessage={nameValidation}
                        className="h-8"
                    />
                </div>
                <div>
                    <Dropdown
                        variant="trigger"
                        items={apiTypes}
                        title="API category *"
                        onChange={setApiType}
                        selectedItems={appUserState.category}
                        validationMessage={categoryValidation}
                        className="h-8"
                        text="Select option"
                    />
                </div>
                <div>
                    <Dropdown
                        variant="trigger"
                        items={permissions}
                        title="Permissions *"
                        onChange={setPermissions}
                        selectedItems={appUserState.permissions}
                        validationMessage={permissionsValidation}
                        className="h-8"
                    />
                </div>
            </div>
            <div className="flex flex-col gap-4 pl-6 py-6">
                <div className="flex flex-col gap-2">
                    <Label text="App user ID" />
                    <div className="flex gap-2 text-sf-gray-500 text-12 text-nowrap">
                        {appUserState.app_user_id}
                        <CopyIcon value={appUserState.app_user_id} />
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <Label text="App user secret" />
                    <div className="flex gap-2 text-sf-gray-500 text-12 text-nowrap">
                        {appUserState.app_user_secret}
                        <CopyIcon value={appUserState.app_user_secret} />
                    </div>
                </div>
            </div>
        </div>
    );
});

const CreateKeyPopup: FC<CreateKeyPopupProps> = memo(({ open, onClose, appUser, createAppUser, validateAppUser }) => {
    const [appUserState, setAppUserState] = useState<AppUser>(appUser);
    const [validations, setValidations] = useState<AppUserValidation[]>([]);
    const onSave = useCallback(() => {
        const appUserValidations = validateAppUser(appUserState);
        setValidations(appUserValidations);
        if (appUserValidations.length === 0) {
            createAppUser(appUserState);
        }
    }, [appUserState, createAppUser, validateAppUser]);

    return (
        <Popup
            open={open}
            title="Create Key"
            popupClassName={'w-[584px]'}
            contentClassName={'py-[0]'}
            ContentComponent={CreateKeyContent}
            FooterComponent={CreateKeyFooter}
            onClose={onClose}
            ContentComponentProps={{ appUserState, setAppUserState, validations }}
            FooterComponentProps={{ onClose, onSave }}
        />
    );
});

export default CreateKeyPopup;
